import type { VisualShape } from 'types'
import type { ShapeContext, ShapeTarget, ShapeResolver } from './types'

const registry = new Map<VisualShape, ShapeResolver>()

/**
 * Register a shape resolver so it can be resolved by name.
 * @param name — Unique shape identifier
 * @param fn — Pure function mapping {@link ShapeContext} → {@link ShapeTarget}
 * @throws If the shape name has already been registered
 */
export function registerShape(name: VisualShape, fn: ShapeResolver): void {
  if (registry.has(name)) throw new Error(`Shape "${name}" already registered`)
  registry.set(name, fn)
}

/**
 * Resolve a shape target for a particle by name.
 * @param name — Registered shape name
 * @param ctx — Per-particle context
 * @returns Computed {@link ShapeTarget}; falls back to orb if name is unregistered
 */
export function resolveShape(name: VisualShape, ctx: ShapeContext): ShapeTarget {
  const fn = registry.get(name)
  if (fn) return fn(ctx)
  // Fallback orb
  const cx = ctx.width / 2, cy = ctx.height / 2
  const a = ctx.index * 0.5 + ctx.time * 0.001
  const d = 50 + Math.sin(ctx.index) * 30
  return { x: cx + Math.cos(a) * d, y: cy + Math.sin(a) * d, spring: 0.08, friction: 0.85, noise: 0.02 }
}

/** @returns Array of all registered shape names. */
export function getRegisteredShapes(): VisualShape[] {
  return Array.from(registry.keys())
}

/** Check whether a shape name has been registered. */
export function hasShape(name: VisualShape): boolean {
  return registry.has(name)
}
